import { useEffect, useState } from 'react';
import { testiStampa } from '../../api/testiStampa';
import type { TestoStampaLetto } from '../../api/testiStampa';
import { usePuo } from '../sessione/ContestoSessione';
import { ModelloEDizionario } from './ModelloEDizionario';

const GRUPPI = [
  {
    chiave: 'Condizioni',
    titolo: 'Condizioni, oneri ed esclusioni',
    spiegazione:
      'Il blocco in fondo all’offerta. Un titolo apre una sezione, un capoverso è un testo corrente, una voce è un punto elenco.',
  },
  /*  Le MODALITA' DI PAGAMENTO non stanno piu' qui. Dal 21/09/2026 arrivano da Mago
      (MA_PaymentTerms) e le gestisce l'amministrazione di la', una volta sola, per
      tutti i documenti: riscriverle anche qui vorrebbe dire due elenchi che prima o poi
      dicono due cose diverse sulla stessa offerta.

      Le righe scritte prima di quel giorno restano nel database e non si vedono piu':
      non servono a niente, e mostrarle inviterebbe a cambiarle senza che cambi nulla.  */
  {
    chiave: 'Lettera',
    titolo: 'Lettere di accompagnamento',
    spiegazione: 'Il capoverso di apertura, quello che precede l’elenco delle voci.',
  },
] as const;

const FORME = [
  { chiave: 'Paragrafo', etichetta: 'Capoverso' },
  { chiave: 'Voce', etichetta: 'Punto elenco' },
  { chiave: 'Titolo', etichetta: 'Titolo di sezione' },
] as const;

/*  I testi fissi che finiscono in offerta.

    Prima di questa schermata le condizioni generali erano dentro il modello Word, e
    cambiarne una voleva dire aprire il documento, correggerlo e ricaricarlo: un lavoro
    da chi sa dove sta il file, non da chi sa cosa deve dire la clausola.

    Due permessi, e la separazione è esplicita nella riunione: spuntare quali clausole
    stampare lo fa chi compila un preventivo, riscrivere cosa dicono no. Chi non ha
    GestisciTestiStampa apre questa schermata in sola lettura — che è utile lo stesso,
    perché prima di spuntare una voce si vuole leggerla per intero.

    Niente cancellazione, e va spiegato invece di lasciarlo scoprire: una clausola citata
    da un'offerta già uscita non si può togliere senza perdere la risposta a «cosa
    avevamo scritto». Si ritira, sparisce da chi compila, e resta leggibile qui.
*/
export function TestiStampa() {
  const puo = usePuo();
  const modificabili = puo('GestisciTestiStampa');

  const [testi, setTesti] = useState<TestoStampaLetto[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [inModifica, setInModifica] = useState<string | null>(null);
  const [bozza, setBozza] = useState('');

  const [gruppoNuovo, setGruppoNuovo] = useState<string>('Condizioni');
  const [formaNuova, setFormaNuova] = useState<string>('Voce');
  const [testoNuovo, setTestoNuovo] = useState('');
  const [predefinitoNuovo, setPredefinitoNuovo] = useState(false);
  const [categoriaNuova, setCategoriaNuova] = useState('');
  const [categoriaBozza, setCategoriaBozza] = useState('');

  const ricarica = () => {
    testiStampa
      .elenco(true)
      .then(setTesti)
      .catch((e: Error) => setErrore(e.message));
  };

  useEffect(ricarica, []);

  const esegui = (promessa: Promise<unknown>) => {
    setInCorso(true);
    setErrore(null);
    promessa
      .then(() => ricarica())
      .catch((e: Error) => setErrore(e.message))
      .finally(() => setInCorso(false));
  };

  /*  Solo le condizioni hanno titoli e punti elenco: gli altri due gruppi sono capoversi
      e basta. La forma va decisa qui e non solo nascondendo la casella — nascondendola e
      lasciando il valore com'era, chi passava da «Condizioni / punto elenco» a «Modalità
      di pagamento» mandava al server una forma che quel gruppo rifiuta, e si prendeva un
      errore per una scelta che non aveva più davanti.  */
  const forma = gruppoNuovo === 'Condizioni' ? formaNuova : 'Paragrafo';

  const aggiungi = (evento: React.FormEvent) => {
    evento.preventDefault();
    esegui(
      testiStampa
        .aggiungi({
          gruppo: gruppoNuovo,
          forma: forma,
          testo: testoNuovo,
          // Un titolo apre una sezione: non è una clausola, quindi non si spunta.
          predefinito: forma === 'Titolo' ? false : predefinitoNuovo,
          //  Solo le condizioni si dividono: una lettera se ne sceglie una sola, e
          //  filtrare un elenco da cui si prende un elemento solo non decide niente.
          categoria: gruppoNuovo === 'Condizioni' ? categoriaNuova : null,
        })
        .then(() => {
          setTestoNuovo('');
          setPredefinitoNuovo(false);
          /*  La categoria NON si svuota: chi sta scrivendo le esclusioni elettriche ne
              scrive sei di fila, e ribatterla ogni volta sarebbe il gesto in piu' per
              cinque volte su sei.  */
        }),
    );
  };

  const salvaCorrezione = (testo: TestoStampaLetto) =>
    esegui(
      testiStampa
        .correggi(testo.id, {
          testo: bozza,
          predefinito: testo.predefinito,
          categoria: categoriaBozza,
        })
        .then(() => setInModifica(null)),
    );

  /*  Le categorie gia' in uso, per suggerirle. Si leggono dalle voci e non da un elenco
      a parte: «la possibilita' per il cliente di aggiungerne quante vuole» vuol dire che
      l'elenco e' quello che c'e' scritto, e una tabella di categorie sarebbe una seconda
      verita' da tenere d'accordo con la prima.  */
  const categorieInUso = [...new Set((testi ?? [])
    .map((t) => t.categoria)
    .filter((c): c is string => c !== null && c !== ''))].sort();

  return (
    <section className="pagina">
      <header className="testata-pagina">
        <div>
          <h1>Testi dell'offerta</h1>
          <p>
            Le parole che escono su ogni offerta: condizioni, oneri, esclusioni, modalità
            di pagamento e lettera di accompagnamento. Chi compila un preventivo le spunta
            e basta; qui si decide cosa dicono.
          </p>
          <p className="nota">
            Correggere una voce la cambia anche sui preventivi ancora in redazione, ed è
            quello che serve quando si scopre un refuso. Le offerte già mandate non
            cambiano — non perché il testo sia copiato, ma perché di quelle si conserva il
            PDF spedito.
          </p>
          {!modificabili && (
            <p className="nota">
              Sola lettura: per riscrivere una voce serve il permesso sui testi di stampa.
            </p>
          )}
        </div>
      </header>

      {errore !== null && <p className="errore">{errore}</p>}

      <ModelloEDizionario modificabile={modificabili} />

      {modificabili && (
        <form className="testo-nuovo" onSubmit={aggiungi}>
          <div className="riga-campi">
            <label>
              Gruppo
              <select value={gruppoNuovo} onChange={(e) => setGruppoNuovo(e.target.value)}>
                {GRUPPI.map((g) => (
                  <option key={g.chiave} value={g.chiave}>
                    {g.titolo}
                  </option>
                ))}
              </select>
            </label>

            {/*  Solo le condizioni hanno titoli e punti elenco: gli altri due gruppi sono
                 capoversi e basta, e offrire una scelta che il server rifiuta sarebbe un
                 modo di far sbagliare.  */}
            {gruppoNuovo === 'Condizioni' && (
              <label>
                Forma
                <select value={formaNuova} onChange={(e) => setFormaNuova(e.target.value)}>
                  {FORME.map((f) => (
                    <option key={f.chiave} value={f.chiave}>
                      {f.etichetta}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {/*  LA CATEGORIA, chiesta in riunione il 01/10/2026: le esclusioni di un
                 impianto elettrico non sono quelle di uno meccanico.

                 Casella libera con i suggerimenti e non una tendina chiusa: «la
                 possibilita' per il cliente di aggiungerne quante vuole». Vuota vuol dire
                 «vale sempre», che e' il caso normale e il comportamento di prima — la
                 garanzia e la validita' non sono ne' elettriche ne' meccaniche.  */}
            {gruppoNuovo === 'Condizioni' && (
              <label>
                Categoria
                <input
                  list="categorie-testi"
                  maxLength={40}
                  value={categoriaNuova}
                  onChange={(e) => setCategoriaNuova(e.target.value)}
                  placeholder="vale sempre"
                />
              </label>
            )}

            {/*  «In partenza» vale PER LA CATEGORIA della clausola: su un'offerta
                 meccanica arrivano spuntate le meccaniche, piu' quelle senza categoria
                 che valgono per tutti i lavori. E' quello che l'etichetta dice, perche'
                 «in partenza» e basta faceva pensare a «su ogni offerta».  */}
            {forma !== 'Titolo' && (
              <label className="spunta">
                <input
                  type="checkbox"
                  checked={predefinitoNuovo}
                  onChange={(e) => setPredefinitoNuovo(e.target.checked)}
                />
                {categoriaNuova.trim() === ''
                  ? 'Spuntata in partenza su ogni offerta'
                  : `Spuntata in partenza sulle offerte «${categoriaNuova.trim()}»`}
              </label>
            )}
          </div>

          <textarea
            value={testoNuovo}
            onChange={(e) => setTestoNuovo(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Il testo come deve uscire in offerta."
            required
          />

          <button type="submit" className="primario" disabled={inCorso || testoNuovo.trim() === ''}>
            Aggiungi
          </button>
        </form>
      )}

      {/*  L'elenco dei suggerimenti sta qui, fuori dai due moduli, perche' lo usano tutti
           e due: quello che aggiunge e quello che corregge.  */}
      <datalist id="categorie-testi">
        {categorieInUso.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>

      {testi === null && <p className="attesa">Lettura…</p>}

      {testi !== null &&
        GRUPPI.map((gruppo) => {
          const voci = testi
            .filter((t) => t.gruppo === gruppo.chiave)
            .sort((a, b) => a.posizione - b.posizione);

          return (
            <section key={gruppo.chiave} className="gruppo-testi">
              <h2>{gruppo.titolo}</h2>
              <p className="nota">{gruppo.spiegazione}</p>

              {voci.length === 0 && <p className="nota">Nessuna voce.</p>}

              <ul className="elenco-testi">
                {voci.map((voce) => (
                  <li key={voce.id} className={voce.attivo ? '' : 'ritirata'}>
                    <div className="capo">
                      <span className={`forma ${voce.forma.toLowerCase()}`}>
                        {FORME.find((f) => f.chiave === voce.forma)?.etichetta ?? voce.forma}
                      </span>
                      {voce.categoria !== null && (
                        <span className="marchietto categoria">{voce.categoria}</span>
                      )}
                      {voce.predefinito && (
                        <span className="marchietto">
                          in partenza{voce.categoria !== null && ` sulle ${voce.categoria.toLowerCase()}`}
                        </span>
                      )}
                      {!voce.attivo && <span className="marchietto ritirata">ritirata</span>}
                    </div>

                    {inModifica === voce.id ? (
                      <>
                        <textarea
                          value={bozza}
                          onChange={(e) => setBozza(e.target.value)}
                          rows={4}
                          maxLength={4000}
                        />
                        {voce.gruppo === 'Condizioni' && (
                          <label className="categoria-voce">
                            Categoria
                            <input
                              list="categorie-testi"
                              maxLength={40}
                              value={categoriaBozza}
                              onChange={(e) => setCategoriaBozza(e.target.value)}
                              placeholder="vale sempre"
                            />
                          </label>
                        )}
                        <div className="comandi">
                          <button
                            type="button"
                            className="primario"
                            disabled={inCorso}
                            onClick={() => salvaCorrezione(voce)}
                          >
                            Salva
                          </button>
                          <button type="button" onClick={() => setInModifica(null)}>
                            Annulla
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="testo">{voce.testo}</p>
                        {modificabili && (
                          <div className="comandi">
                            <button
                              type="button"
                              onClick={() => {
                                setInModifica(voce.id);
                                setBozza(voce.testo);
                                setCategoriaBozza(voce.categoria ?? '');
                              }}
                            >
                              Correggi
                            </button>
                            <button
                              type="button"
                              disabled={inCorso}
                              onClick={() => esegui(testiStampa.ritira(voce.id, voce.attivo))}
                            >
                              {voce.attivo ? 'Ritira' : 'Rimetti in elenco'}
                            </button>
                            {voce.forma !== 'Titolo' && (
                              <button
                                type="button"
                                disabled={inCorso}
                                onClick={() =>
                                  esegui(
                                    testiStampa.correggi(voce.id, {
                                      testo: voce.testo,
                                      predefinito: !voce.predefinito,
                                      categoria: voce.categoria,
                                    }),
                                  )
                                }
                              >
                                {voce.predefinito ? 'Non in partenza' : 'Spunta in partenza'}
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
    </section>
  );
}
